import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';
import { clinicDayNumber, clinicWeekdayIndex, sameClinicDay } from '@/utils/calendarClinicTime';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function WeekStrip({
  days,
  selected,
  counts,
  onSelect,
}: {
  days: Date[];
  selected: Date;
  counts: number[];
  onSelect: (date: Date) => void;
}) {
  const { t } = useTranslation();
  const today = new Date();

  return (
    <Card className="p-s2" data-testid="calendar-week-strip">
      <div className="grid grid-cols-7 gap-s1">
        {days.map((day, index) => {
          const current = sameClinicDay(day, selected);
          const isToday = sameClinicDay(day, today);
          const dow = clinicWeekdayIndex(day);
          const count = counts[index] ?? 0;
          return (
            <button
              key={day.toISOString()}
              type="button"
              aria-current={current ? 'date' : undefined}
              onClick={() => onSelect(day)}
              className={cn(
                'flex min-w-0 flex-col items-center rounded-ctl px-0.5 py-s2',
                current ? 'bg-ds-teal-tint text-ds-teal' : 'text-ds-muted',
              )}
            >
              <span className={cn(TYPE.caption, 'font-semibold')}>
                {t(`appointments.weekdays_short.${WEEKDAY_KEYS[dow]}`)}
              </span>
              <span
                className={cn(
                  'mt-0.5 text-[18px] font-semibold tabular-nums leading-6',
                  current || isToday ? 'text-ds-teal' : 'text-ds-text',
                )}
              >
                {clinicDayNumber(day)}
              </span>
              <span className="mt-s1 flex h-1.5 justify-center gap-0.5" aria-hidden>
                {Array.from({ length: Math.min(3, count) }).map((_, dot) => (
                  <i key={dot} className="block h-1 w-1 rounded-full bg-ds-teal" />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
